import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  appendColumnDefinitionsBlock,
  type ColumnInfo,
  type RowInfo,
  serializeTableToText,
} from '~/utils'

import { useCommentGenerationContext } from '../providers/CommentGenerationProvider'
import { buildGroupColumnDefinitions } from '../utils'

import { useGroupAndAllNameMap } from './useGroupAndAllNameMap'
import { useTabulatedGroups } from './useTabulatedGroups'

type SerializeGroupTable = <K extends string>(
  columns: ColumnInfo<K>[],
  tableInfo: RowInfo<K>[],
) => string

/**
 * AIコメント生成に渡す tabulationData を組み立てます。
 *
 * 表そのものは serializeTableToText の TSV ですが、その後ろに「列の定義」ブロックを足します。
 * TSV には列名（「全体」「グループ1」）しか入らず、そのグループが何で構成されているかが伝わらないため、
 * これが無いと analyzer 側 columnComparison プロンプトの
 * 「各列の定義が入力データに含まれている場合は…」が発火しません。
 *
 * 定義が1件も無ければブロックごと省略されるので、同プロンプトの「無い場合は触れない」側が効きます。
 *
 * @returns columns / tableInfo を受け取って tabulationData 文字列を返す関数。
 */
export const useSerializeGroupTable = (): SerializeGroupTable => {
  const { t } = useTranslation()
  const { groupDefinitionMap } = useCommentGenerationContext()
  const groupAndAllNameMap = useGroupAndAllNameMap()
  const tabulatedGroups = useTabulatedGroups()

  return React.useCallback<SerializeGroupTable>(
    (columns, tableInfo) =>
      appendColumnDefinitionsBlock(
        serializeTableToText(columns, tableInfo),
        t('column_definitions'),
        buildGroupColumnDefinitions({
          groupAndAllNameMap,
          groupDefinitionMap,
          groups: tabulatedGroups,
        }),
      ),
    [groupAndAllNameMap, groupDefinitionMap, t, tabulatedGroups],
  )
}
